/**
 * Access Control Service
 * Manages user permissions and file sharing for BigSplice
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../aws-config';

// Access Control API endpoint
const ACCESS_CONTROL_API_ENDPOINT = lambdaEndpoints.accessControlApi;

/**
 * User permission structure - matches API response
 * API returns permissions as a flat dict: { dashboard: true, admin: true, ... }
 */
export interface UserPermissions {
  [featureName: string]: boolean;
}

/**
 * Response for getting user permissions
 */
export interface GetPermissionsResponse {
  success: boolean;
  permissions?: UserPermissions;
  error?: string;
}

/**
 * Response for setting feature permission
 */
export interface SetPermissionResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Response for getting all users' permissions (admin only)
 */
export interface GetAllPermissionsResponse {
  success: boolean;
  users?: UserPermissions[];
  count?: number;
  error?: string;
}

/**
 * File share permission types
 */
export type SharePermission = 'read' | 'write' | 'admin';

/**
 * Shared file metadata
 */
export interface SharedFile {
  file_key: string;
  shared_by: string;
  shared_with: string;
  permissions: SharePermission[];
  shared_at: string;
  file_name?: string;
  file_size?: number;
}

/**
 * Response for sharing a file
 */
export interface ShareFileResponse {
  success: boolean;
  message?: string;
  share?: SharedFile;
  error?: string;
}

/**
 * Response for getting files shared with user
 */
export interface GetSharedFilesResponse {
  success: boolean;
  files?: SharedFile[];
  count?: number;
  error?: string;
}

/**
 * Response for revoking file share
 */
export interface RevokeShareResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Invitation structure
 */
export interface Invite {
  invite_id: string;
  org_id: string;
  org_name: string;
  inviter_email: string;
  invitee_email?: string;
  role: string;
  status?: string;
  created_at: string;
}

/**
 * Response for creating an invite
 */
export interface CreateInviteResponse {
  success: boolean;
  message?: string;
  invite_id?: string;
  invitee_email?: string;
  invite_link?: string;
  email_sent?: boolean;
  error?: string;
}

/**
 * Response for getting invites
 */
export interface GetInvitesResponse {
  success: boolean;
  invites?: Invite[];
  count?: number;
  error?: string;
}

/**
 * Response for accepting/declining invite
 */
export interface InviteActionResponse {
  success: boolean;
  message?: string;
  org_id?: string;
  invite_link?: string;
  email_sent?: boolean;
  error?: string;
}

/**
 * Response for removing a member
 */
export interface RemoveMemberResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * File access grant (admin-controlled file visibility)
 */
export interface FileAccessGrant {
  path: string;
  type: 'file' | 'folder';
  granted_by?: string;
  granted_at?: string;
}

/**
 * Response for getting file access grants
 */
export interface GetFileAccessResponse {
  success: boolean;
  user_id?: string;
  grants?: FileAccessGrant[];
  error?: string;
}

/**
 * Response for setting file access grants
 */
export interface SetFileAccessResponse {
  success: boolean;
  message?: string;
  grants_count?: number;
  error?: string;
}

/**
 * Response for checking file access
 */
export interface CheckFileAccessResponse {
  success: boolean;
  user_id?: string;
  file_key?: string;
  has_access?: boolean;
  error?: string;
}

/**
 * Public invite lookup response (no auth required)
 */
export interface InviteLookupResponse {
  success: boolean;
  invite_id?: string;
  org_id?: string;
  org_name?: string;
  invitee_email?: string;
  role?: string;
  status?: string;
  inviter_email?: string;
  error?: string;
}

/**
 * Get authentication headers with Cognito JWT token
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (!token) {
      console.warn('No authentication token available');
      return {
        'Content-Type': 'application/json',
      };
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {
      'Content-Type': 'application/json',
    };
  }
}

/**
 * Access Control Service
 * Provides methods for managing user permissions and file sharing
 */
class AccessControlService {

  /**
   * Get permissions for a specific user
   * @param userId - The user ID to get permissions for
   * @returns User permissions object
   */
  async getUserPermissions(userId: string): Promise<GetPermissionsResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/permissions/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get user permissions' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      // API returns { user_id, permissions } - add success flag for consistency
      return {
        success: true,
        permissions: data.permissions,
      };
    } catch (error) {
      console.error('Error getting user permissions:', error);
      return {
        success: false,
        error: `Failed to get user permissions: ${error}`,
      };
    }
  }

  /**
   * Set a specific feature permission for a user
   * @param userId - The user ID to set permission for
   * @param feature - The feature name (e.g., 'ai_queries', 'file_upload', 'data_export')
   * @param enabled - Whether the feature should be enabled or disabled
   * @returns Success response
   */
  async setFeaturePermission(userId: string, feature: string, enabled: boolean): Promise<SetPermissionResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/permissions/${encodeURIComponent(userId)}/${encodeURIComponent(feature)}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ enabled }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to set feature permission' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return { success: true, ...data };
    } catch (error) {
      console.error('Error setting feature permission:', error);
      return {
        success: false,
        error: `Failed to set feature permission: ${error}`,
      };
    }
  }

  /**
   * Get all users' permissions (admin only)
   * @returns List of all users with their permissions
   */
  async getAllPermissions(): Promise<GetAllPermissionsResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/permissions/all`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get all permissions' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return { success: true, users: data.users, count: data.count };
    } catch (error) {
      console.error('Error getting all permissions:', error);
      return {
        success: false,
        error: `Failed to get all permissions: ${error}`,
      };
    }
  }

  /**
   * Share a file with another user
   * @param fileKey - The S3 key of the file to share
   * @param shareWithUserId - The user ID to share with
   * @param permissions - Array of permissions to grant (e.g., ['read'], ['read', 'write'])
   * @returns Share response with metadata
   */
  async shareFile(fileKey: string, shareWithUserId: string, permissions: string[]): Promise<ShareFileResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/files/share`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_key: fileKey,
          share_with_user_id: shareWithUserId,
          permissions: permissions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to share file' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return { success: true, ...data };
    } catch (error) {
      console.error('Error sharing file:', error);
      return {
        success: false,
        error: `Failed to share file: ${error}`,
      };
    }
  }

  /**
   * Get all files shared with the current user
   * @returns List of files shared with the user
   */
  async getSharedWithMe(): Promise<GetSharedFilesResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/files/shared-with-me`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get shared files' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return { success: true, files: data.files, count: data.count };
    } catch (error) {
      console.error('Error getting shared files:', error);
      return {
        success: false,
        error: `Failed to get shared files: ${error}`,
      };
    }
  }

  /**
   * Get all shares for a specific file (files shared by the current user)
   * @param fileKey - The S3 key of the file
   * @returns List of users the file is shared with
   */
  async getFileShares(fileKey: string): Promise<GetSharedFilesResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/files/shares/${encodeURIComponent(fileKey)}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get file shares' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return { success: true, files: data.files, count: data.count };
    } catch (error) {
      console.error('Error getting file shares:', error);
      return {
        success: false,
        error: `Failed to get file shares: ${error}`,
      };
    }
  }

  /**
   * Revoke file sharing for a specific user
   * @param fileKey - The S3 key of the file
   * @param userId - The user ID to revoke access from
   * @returns Revoke response
   */
  async revokeShare(fileKey: string, userId: string): Promise<RevokeShareResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/files/share/${encodeURIComponent(fileKey)}/${encodeURIComponent(userId)}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to revoke share' }));
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      return { success: true, ...data };
    } catch (error) {
      console.error('Error revoking share:', error);
      return {
        success: false,
        error: `Failed to revoke share: ${error}`,
      };
    }
  }

  /**
   * Utility: Format date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Utility: Check if user has a specific permission
   */
  hasPermission(permissions: Record<string, boolean> | undefined, feature: string): boolean {
    if (!permissions) {
      return false;
    }
    return permissions[feature] === true;
  }

  /**
   * Get all users with formatted permissions for the pivot grid
   * Includes email and all feature permissions
   */
  async listUsersWithPermissions(): Promise<any[]> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/permissions/all`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to list users' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error listing users:', error);
      throw error;
    }
  }

  /**
   * Update a single permission for a user
   */
  async updatePermission(userId: string, feature: string, enabled: boolean): Promise<void> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/permissions/${encodeURIComponent(userId)}/${encodeURIComponent(feature)}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ enabled }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update permission' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating permission:', error);
      throw error;
    }
  }

  /**
   * Bulk update all permissions for a user
   * Calls individual update endpoint for each permission
   */
  async bulkUpdatePermissions(userId: string, permissions: Record<string, boolean>): Promise<void> {
    const updates = Object.entries(permissions).map(([feature, enabled]) =>
      this.updatePermission(userId, feature, enabled)
    );
    await Promise.all(updates);
  }

  /**
   * Enable all features for a user
   */
  async enableAllFeatures(userId: string): Promise<void> {
    const allPermissions: Record<string, boolean> = {
      dashboard: true,
      upload: true,
      analyze: true,
      visualize: true,
      introspect: true,
      'developer-api': true,
      'ai-connections': true,
      integrations: true,
      usage: true,
      profile: true,
      'stats-dashboard': true,
      admin: true,
    };

    await this.bulkUpdatePermissions(userId, allPermissions);
  }

  /**
   * Disable all features for a user (except profile - keep at least one)
   */
  async disableAllFeatures(userId: string): Promise<void> {
    const allPermissions: Record<string, boolean> = {
      dashboard: false,
      upload: false,
      analyze: false,
      visualize: false,
      introspect: false,
      'developer-api': false,
      'ai-connections': false,
      integrations: false,
      usage: false,
      profile: true, // Keep profile enabled
      'stats-dashboard': false,
      admin: false,
    };

    await this.bulkUpdatePermissions(userId, allPermissions);
  }

  /**
   * Export permissions to CSV
   */
  exportToCSV(users: any[]): string {
    const features = [
      'dashboard',
      'upload',
      'analyze',
      'visualize',
      'introspect',
      'developer-api',
      'ai-connections',
      'integrations',
      'usage',
      'profile',
      'stats-dashboard',
      'admin',
    ];

    // CSV Header
    const header = ['Email', ...features.map(f => f.charAt(0).toUpperCase() + f.slice(1).replace(/-/g, ' '))];
    const rows = users.map(user => {
      const row = [user.email];
      features.forEach(feature => {
        const hasFeature = user.features && user.features[feature] === true;
        row.push(hasFeature ? 'Yes' : 'No');
      });
      return row;
    });

    const csvContent = [
      header.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    return csvContent;
  }

  /**
   * Download CSV file
   */
  downloadCSV(users: any[]): void {
    const csv = this.exportToCSV(users);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `permissions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Create an invitation to join the organization
   * @param email - Email address of the user to invite
   * @param role - Role for the invited user (default: 'member')
   */
  async createInvite(email: string, role: string = 'member'): Promise<CreateInviteResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, role }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error creating invite:', error);
      return {
        success: false,
        error: `Failed to create invite: ${error}`,
      };
    }
  }

  /**
   * Lookup invite details by invite ID (PUBLIC - no auth required)
   * Used when a user clicks an invite link to see the org name and role before signing up.
   * @param inviteId - The invite ID from the URL
   */
  async lookupInvite(inviteId: string): Promise<InviteLookupResponse> {
    try {
      // This is a public endpoint - no auth headers needed
      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites/lookup/${encodeURIComponent(inviteId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error looking up invite:', error);
      return {
        success: false,
        error: `Failed to lookup invite: ${error}`,
      };
    }
  }

  /**
   * Get pending invitations for the current user
   */
  async getPendingInvites(): Promise<GetInvitesResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, invites: data.invites || [], count: data.count || 0 };
    } catch (error) {
      console.error('Error getting pending invites:', error);
      return {
        success: false,
        error: `Failed to get pending invites: ${error}`,
      };
    }
  }

  /**
   * Get invitations sent by the current admin
   */
  async getSentInvites(): Promise<GetInvitesResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites/sent`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, invites: data.invites || [], count: data.count || 0 };
    } catch (error) {
      console.error('Error getting sent invites:', error);
      return {
        success: false,
        error: `Failed to get sent invites: ${error}`,
      };
    }
  }

  /**
   * Accept an invitation
   */
  async acceptInvite(orgId: string, inviteId: string): Promise<InviteActionResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites/accept`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ org_id: orgId, invite_id: inviteId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error accepting invite:', error);
      return {
        success: false,
        error: `Failed to accept invite: ${error}`,
      };
    }
  }

  /**
   * Decline an invitation
   */
  async declineInvite(orgId: string, inviteId: string): Promise<InviteActionResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites/decline`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ org_id: orgId, invite_id: inviteId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error declining invite:', error);
      return {
        success: false,
        error: `Failed to decline invite: ${error}`,
      };
    }
  }

  /**
   * Remove a member from the organization
   */
  async removeMember(userId: string): Promise<RemoveMemberResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/members/${encodeURIComponent(userId)}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error removing member:', error);
      return {
        success: false,
        error: `Failed to remove member: ${error}`,
      };
    }
  }

  /**
   * Resend an invitation (rate limited to 1 per 15 minutes)
   */
  async resendInvite(inviteId: string): Promise<InviteActionResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/invites/resend`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ invite_id: inviteId }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error resending invite:', error);
      return {
        success: false,
        error: `Failed to resend invite: ${error}`,
      };
    }
  }

  // =========================================================================
  // FILE ACCESS CONTROL (Admin-controlled file visibility for non-admins)
  // =========================================================================

  /**
   * Get file access grants for a user (admin only)
   * @param userId - The user ID to get grants for
   */
  async getFileAccess(userId: string): Promise<GetFileAccessResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/file-access/${encodeURIComponent(userId)}`,
        {
          method: 'GET',
          headers,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error getting file access:', error);
      return {
        success: false,
        error: `Failed to get file access: ${error}`,
      };
    }
  }

  /**
   * Set file access grants for a user (admin only)
   * Replaces all existing grants with the new set
   * @param userId - The user ID to set grants for
   * @param grants - Array of file/folder grants
   */
  async setFileAccess(userId: string, grants: FileAccessGrant[]): Promise<SetFileAccessResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/file-access/${encodeURIComponent(userId)}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ grants }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error setting file access:', error);
      return {
        success: false,
        error: `Failed to set file access: ${error}`,
      };
    }
  }

  /**
   * Delete all file access grants for a user (admin only)
   * @param userId - The user ID to remove grants from
   */
  async deleteFileAccess(userId: string): Promise<SetFileAccessResponse> {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${ACCESS_CONTROL_API_ENDPOINT}/file-access/${encodeURIComponent(userId)}`,
        {
          method: 'DELETE',
          headers,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error deleting file access:', error);
      return {
        success: false,
        error: `Failed to delete file access: ${error}`,
      };
    }
  }

  /**
   * Check if a user has access to a specific file
   * @param fileKey - The S3 file key to check access for
   * @param userId - Optional user ID (defaults to current user)
   */
  async checkFileAccess(fileKey: string, userId?: string): Promise<CheckFileAccessResponse> {
    try {
      const headers = await getAuthHeaders();

      const body: Record<string, string> = { file_key: fileKey };
      if (userId) {
        body.user_id = userId;
      }

      const response = await fetch(`${ACCESS_CONTROL_API_ENDPOINT}/file-access/check`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error checking file access:', error);
      return {
        success: false,
        error: `Failed to check file access: ${error}`,
      };
    }
  }

  /**
   * Filter a list of files based on user's file access grants
   * Used client-side for immediate filtering
   */
  filterFilesByGrants(files: { key: string }[], grants: FileAccessGrant[]): { key: string }[] {
    if (!grants || grants.length === 0) {
      return []; // STRICT mode: no grants = no files
    }

    return files.filter(file => {
      const fileKey = file.key;

      for (const grant of grants) {
        if (grant.type === 'folder') {
          // Folder grant: check prefix match
          if (fileKey.startsWith(grant.path)) {
            return true;
          }
        } else {
          // File grant: exact match
          if (fileKey === grant.path) {
            return true;
          }
        }
      }

      return false;
    });
  }
}

// Export singleton instance
export const accessControlService = new AccessControlService();
